import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { useLoyaltyConfig, computePointsForAmount } from '@/hooks/useLoyalty';
import { useTranslations } from '@/hooks/useTranslations';

interface PointsEarnInfoProps {
  amount: number;
  className?: string;
}

/**
 * Inline-Erklärung für Big-Punkte: zeigt wie viele Punkte
 * der Kunde mit diesem Kauf sammelt und was sie bedeuten.
 */
export default function PointsEarnInfo({ amount, className = '' }: PointsEarnInfoProps) {
  const { settings, loading } = useLoyaltyConfig();
  const { localePath } = useLanguage();
  const { t } = useTranslations();
  if (loading || !settings || !settings.is_enabled) return null;
  const points = computePointsForAmount(amount, settings, 1);
  if (points <= 0) return null;

  const formattedPoints = points.toLocaleString('de-CH');
  // Title contains <accent>...</accent> wrapping the points part
  const title = t('loyalty.earn_info_title').replace('{points}', formattedPoints);
  const [before, rest] = title.split('<accent>');
  const [accent, after] = (rest ?? '').split('</accent>');

  return (
    <div
      className={`flex items-start gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 ${className}`}
    >
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
        <Sparkles className="h-3.5 w-3.5" />
      </div>
      <div className="text-xs leading-snug">
        <p className="font-semibold text-foreground">
          {before}
          <span className="text-primary">{accent}</span>
          {after}
        </p>
        <p className="mt-0.5 text-muted-foreground">
          {t('loyalty.earn_info_desc')}{' '}
          <Link to={localePath('/big-punkte')} className="font-medium text-primary underline-offset-2 hover:underline">
            {t('loyalty.learn_more')}
          </Link>
        </p>
      </div>
    </div>
  );
}